import React from 'react';
import clsx from 'clsx';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The visual style variant of the card
   */
  variant?: 'default' | 'outlined' | 'elevated' | 'filled';
  
  /**
   * The size of the card padding
   */
  padding?: 'none' | 'sm' | 'base' | 'lg' | 'xl';
  
  /**
   * The border color theme variant
   */
  borderColor?: 'primary' | 'secondary' | 'tertiary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark' | 'none';
  
  /**
   * Whether the card should be interactive (hover effects)
   */
  interactive?: boolean;
  
  /**
   * Whether the card should have rounded corners
   */
  rounded?: boolean;

  transparent?: boolean;
  
  /**
   * The content of the card
   */
  children: React.ReactNode;
  center?: boolean;
}

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export interface CardBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

/**
 * Card component for displaying content in a contained, elevated surface
 */
export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      padding = 'base',
      transparent = false,
      borderColor,
      interactive = false,
      rounded = true,
      className,
      children,
      center,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={clsx(
          'prometheus-card',
          `prometheus-card--${variant}`,
          `prometheus-card--padding-${padding}`,
          {
            'prometheus-card--interactive': interactive,
            'prometheus-card--rounded': rounded,
            'prometheus-card--center': center,
            [`prometheus-card--border-${borderColor}`]: borderColor,
          },
          className
        )}
        {...props}
        style={{
          ...props.style,
          ...(transparent ? { backgroundColor: 'rgb(from var(--prometheus-bg-primary) r g b / 0.95)' } : {}),
        }}
      >
        {children}
      </div>
    );
  }
);

/**
 * Card header component for displaying title and actions
 */
export const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx('prometheus-card__header', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

/**
 * Card body component for main content
 */
export const CardBody = React.forwardRef<HTMLDivElement, CardBodyProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx('prometheus-card__body', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

/**
 * Card footer component for actions and additional content
 */
export const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx('prometheus-card__footer', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';
CardHeader.displayName = 'CardHeader';
CardBody.displayName = 'CardBody';
CardFooter.displayName = 'CardFooter';

export default Card;